import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { TONE_TEXT } from '../../lib/tone';
import { Icon, type LucideIcon } from '../ui/icon';

type StatTileDelta =
  /** `good` setzen, wenn „runter“ gut ist. */
  | { value: string; direction: 'up' | 'down'; good?: boolean }
  /** Eine Veränderung, die keine Zahl ist: „reißt die Runway auf“. */
  | { text: string; good: boolean };

export interface StatTileProps {
  label: string;
  /** Formatiert übergeben: „12.500 €“, „128“. Nur echte Werte aus CRM, PM oder Finanzen. */
  value: ReactNode;
  icon?: LucideIcon;
  /** Kontext: „diese Woche“. */
  hint?: ReactNode;
  /** Veränderung als Zahl mit Richtung oder als Text; grün, wenn sie gut ist, sonst rot. */
  delta?: StatTileDelta;
  /**
   * Ton des Werts, wenn er selbst eine Aussage trägt (Runway kritisch:
   * `danger`, knapp: `warning`). Ohne Ton bleibt er in der Textfarbe.
   */
  tone?: 'warning' | 'danger' | 'success';
  /** `center` für kompakte Messwerte wie Temperaturen. */
  align?: 'start' | 'center';
  className?: string;
}

/** Eine Kennzahl mit Label in einer eingelassenen Kachel. Drei bis fünf nebeneinander. */
export function StatTile({ label, value, icon, hint, delta, tone, align = 'start', className }: StatTileProps) {
  const gut = delta ? ('text' in delta ? delta.good : (delta.good ?? delta.direction === 'up')) : false;
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5 rounded-lg bg-muted px-4 py-3.5', align === 'center' && 'items-center text-center', className)}>
      <div className="flex items-center gap-1.5 t-label text-muted-foreground">
        {icon && <Icon icon={icon} size={14} />}
        {label}
      </div>
      <div className={cn('whitespace-nowrap t-stat tabular', tone ? TONE_TEXT[tone] : 'text-foreground')}>{value}</div>
      {(hint || delta) && (
        <div className="t-small text-muted-foreground">
          {delta && (
            <span className={cn('font-medium', gut ? 'text-success' : 'text-danger')}>
              {'text' in delta ? delta.text : (delta.direction === 'up' ? '+' : '−') + delta.value}
            </span>
          )}
          {delta && hint ? ' ' : null}
          {hint}
        </div>
      )}
    </div>
  );
}
